import {
  AnnotationWhereGroup,
  isAnnotationWhereGroup,
} from "components/annotations/AnnotationWhereGroup";
import { AioReplacement, isAioReplacement } from "../../additional";
import { isMosaicTrafficLightsValue, MosaicTrafficLightsValue } from "../../traffic-lights";
import { AitRowData, isAitRowData } from "./AitRowData";
import { GridItemTrafficLight, isGridItemTrafficLight } from "./GridItemTrafficLight";

/**
 * A group of rows within the table body.
 *
 * @template T The data type used for comments and replaceable text.
 */

export interface AitRowGroupData<T> {
  /** Unique identifier for the row group. */
  aitid: string;
  /** Optional name for the group. */
  name?: string;
  /** Rows contained in the group. */
  rows: AitRowData<T>[];
  /** Optional comments for the group. */
  comments?: T;
  /** When true, include a space after the last row in the group. */
  spaceAfter?: boolean;
  /** Replacement lists used when expanding repeats in this group. */
  replacements?: AioReplacement<T>[];
  /** Traffic light value representing the group's core status. */
  coreTrafficLight: MosaicTrafficLightsValue;
  /** Rows that have changed and their traffic light info. */
  changedRows?: GridItemTrafficLight[];
  /** Equivalent aitid when this group originates from a template. */
  templateAitId?: string;
  /** Optional annotation at the row-group level. */
  annotation?: AnnotationWhereGroup;
}

/**
 * Type guard that asserts whether a value conforms to the AitRowGroupData shape.
 *
 * @param obj - The object to check
 * @param isT - Type guard function for T
 * @param noColumnIdCheck - Optional flag to skip column ID checks (used for rowGroup templates)
 * @returns boolean
 */
export const isAitRowGroupData = <T>(
  obj: unknown,
  isT: (o: unknown) => o is T,
  noColumnIdCheck?: boolean,
): obj is AitRowGroupData<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check lower interface
  const chkObj = obj as AitRowGroupData<T>;
  if (typeof chkObj.aitid !== "string") return false;
  if (typeof chkObj.name !== "undefined" && typeof chkObj.name !== "string") return false;
  if (
    !Array.isArray(chkObj.rows) ||
    chkObj.rows.some((x) => !isAitRowData(x, isT, noColumnIdCheck))
  )
    return false;
  if (typeof chkObj.comments !== "undefined" && !isT(chkObj.comments)) return false;
  if (typeof chkObj.spaceAfter !== "undefined" && typeof chkObj.spaceAfter !== "boolean")
    return false;
  if (
    typeof chkObj.replacements !== "undefined" &&
    chkObj.replacements.some((x) => !isAioReplacement(x, isT))
  )
    return false;
  if (!isMosaicTrafficLightsValue(chkObj.coreTrafficLight)) return false;
  if (
    typeof chkObj.changedRows !== "undefined" &&
    chkObj.changedRows.some((x) => !isGridItemTrafficLight(x))
  )
    return false;
  if (typeof chkObj.templateAitId !== "undefined" && typeof chkObj.templateAitId !== "string")
    return false;
  if (typeof chkObj.annotation !== "undefined" && !isAnnotationWhereGroup(chkObj.annotation))
    return false;

  // Everything is good
  return true;
};
